import {
  AccordionItemProps,
  AccordionList,
  Line,
  SectionTitle,
  Typography,
  useMobile,
} from '@sonnen/web-ui';
import clsx from 'clsx';
import { memo, useMemo } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute } from '../../constants';
import { CurrencyProperty } from '../../graphql/generated';
import { useCurrentRoute } from '../../hooks/useCurrentRoute';
import { LocaleKey, useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { ModuleName, OrderFlow } from '../../types';
import { formatCurrency } from '../../utils/formatting';
import {
  getPriceAfterFirstYear,
  getSelectedTariffConfiguration,
  moduleAvailable,
} from '../../utils/utils';
import { FillInfoCard } from '../FillInfoCard/FillInfoCard';
import OfferLoadingOrError from '../OfferLoadingOrError/OfferLoadingOrError';
import { OfferSummary } from '../OfferSummary/OfferSummary';
import { SideCard } from '../SideCard/SideCard';
import { SideCardButton } from '../SideCardButton/SideCardButton';
import Tooltip from '../Tooltip/Tooltip';
import styles from './OfferCard.module.css';

const generateListData = (
  t: (key: LocaleKey) => string,
  listEntries: Record<string, string | null>,
  tariff: string,
) => {
  return Object.entries(listEntries).map(([key, value]) => {
    let children = t(`offer.accordion.${key}.${tariff}description` as LocaleKey);
    if (children === `offer.accordion.${key}.${tariff}description`) children = '';

    const item = {
      label: t(`offer.accordion.${key}.${tariff}label` as LocaleKey),
      value: value ?? t(`offer.accordion.${key}.${tariff}value` as LocaleKey),
      children,
      dataTestId: key,
      alwaysOpen: false,
    };

    if (tariff && key === 'purchaseTariffGrossPerKwh') item.alwaysOpen = true;
    return item;
  });
};

const BonusSection = ({ bonusAmount }: { bonusAmount: CurrencyProperty }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.bonusSectionWrapper}>
      <div className={styles.bonusBox}>
        <Typography.Body1>{t('offer.welcomeBonus')}</Typography.Body1>
        <Typography.Label1>{formatCurrency(bonusAmount, 'euro')}</Typography.Label1>
      </div>
      <Tooltip
        content={t('offerCard.bonusTooltipContent')}
        position="bottom"
        ariaLabel={t('offerCard.tooltipAriaLabel')}
        dataTestId="bonus-tooltip"
      />
    </div>
  );
};

const TariffSummary = ({ consumption }: { consumption: number | undefined | null }) => {
  const { t } = useTranslation();
  const { offer } = useOfferContext();
  const { modules, orderFlow } = useAppContext();

  const configuration = getSelectedTariffConfiguration(offer, modules, orderFlow);
  if (!configuration) return false;
  const { tariffChangeFlatXSonnenHomeOneTimeBenefitAmount } = configuration;

  return (
    <FillInfoCard
      dataTestId="tariff-summary"
      className={clsx(styles.tariffSummary, {
        [styles.tariffSummaryWithBonus]: !!tariffChangeFlatXSonnenHomeOneTimeBenefitAmount,
      })}
    >
      <div className={styles.sectionTitleWithTooltip}>
        <SectionTitle>{t('offerCard.yourEstimatedCosts')}</SectionTitle>
        <Tooltip
          content={t('offerCard.tooltipContent')}
          position="bottom"
          ariaLabel={t('offerCard.tooltipAriaLabel')}
          dataTestId="info-tooltip"
        />
      </div>
      <Typography.Body1>
        {t('offerCard.yourEstimatedCostsTagline', { consumption })}
      </Typography.Body1>
      <Line />
      <OfferSummary />
      {tariffChangeFlatXSonnenHomeOneTimeBenefitAmount && (
        <BonusSection bonusAmount={tariffChangeFlatXSonnenHomeOneTimeBenefitAmount} />
      )}
    </FillInfoCard>
  );
};

const OfferCard = () => {
  const mobile = useMobile();
  const currentRoute = useCurrentRoute();
  const { t } = useTranslation();
  const { modules, orderFlow } = useAppContext();
  const { offer, offerLoading, offerError, productConfigurationsInput } = useOfferContext();

  const priceAfterFirstYear =
    getPriceAfterFirstYear(offer, modules) ||
    t('optimizationInfoCard.customer.priceAfterFirstYear');

  const listEntries = useMemo(() => {
    if (!offer) return [];

    let listData: AccordionItemProps[] = [];

    if (orderFlow === OrderFlow.energyFix && offer.energyFix?.configuration) {
      const {
        baseFeeGrossPerMonth,
        fixedCostsGrossPerMonth,
        purchaseTariffGrossPerKwh,
        purchaseTariffGrossWithoutDiscountPerKwh,
        tariffChangeFlatXSonnenHomePerKwhBenefitAmount,
      } = offer.energyFix.configuration;

      const listEntries: Record<string, string | null> = {
        priceDynamic: t(`offer.accordion.priceDynamic.energyFix.value`),
        baseFeeGrossPerMonth: `${formatCurrency(baseFeeGrossPerMonth, 'euro')}/${t('offer.month')}`,
        gridFees: `${formatCurrency(fixedCostsGrossPerMonth, 'euro')}/${t('offer.month')}`,
        minimumContractDuration: t(`offer.accordion.minimumContractDuration.energyFix.value`),
        terminationPeriodOption: t(`offer.accordion.terminationPeriodOption.energyFix.value`),
      };

      const orderedListEntries: Record<string, string | null> = {};

      for (const key in listEntries) {
        orderedListEntries[key] = listEntries[key];
        const value = `${formatCurrency(purchaseTariffGrossPerKwh, 'ct')}/kWh`;

        if (key === 'priceDynamic') {
          if (tariffChangeFlatXSonnenHomePerKwhBenefitAmount) {
            orderedListEntries.purchaseTariffGrossPerKwh = value;
            orderedListEntries.purchaseTariffGrossWithoutDiscountPerKwh = `${formatCurrency(
              purchaseTariffGrossWithoutDiscountPerKwh,
              'ct',
            )}/kWh`;
          } else {
            orderedListEntries.purchaseTariffGrossPerKwhLabelWithoutYear = value;
          }
        }
      }

      listData = generateListData(t, orderedListEntries, 'energyFix.');
    }

    if (
      orderFlow === OrderFlow.energyDynamic &&
      offer.energyDynamic?.configurationWithoutBatteryOptimizationModule
    ) {
      const { baseFeeGrossPerMonth, fixedCostsGrossPerMonth, averagePrices } =
        offer.energyDynamic.configurationWithBatteryOptimizationModule ||
        offer.energyDynamic.configurationWithoutBatteryOptimizationModule;

      const listEntries: Record<string, string | null> = {
        priceDynamic: t(`offer.accordion.priceDynamic.value`),
        purchaseTariffGrossPerKwh: `${formatCurrency(averagePrices?.purchaseTariffGrossPerKwh, 'ct')}/kWh`,
        priceOptimization: t(`offer.accordion.priceOptimization.value`, {
          value: priceAfterFirstYear,
        }),
        baseFeeGrossPerMonth: `${formatCurrency(baseFeeGrossPerMonth, 'euro')}/${t('offer.month')}`,
        gridFees: `${formatCurrency(fixedCostsGrossPerMonth, 'euro')}/${t('offer.month')}`,
        minimumContractDuration: t(`offer.accordion.minimumContractDuration.value`),
        terminationPeriodOption: t(`offer.accordion.terminationPeriodOption.value`),
      };

      const priceOptimizationModuleAdded =
        moduleAvailable(offer, ModuleName.PRICE_OPTIMIZATION) &&
        modules.has(ModuleName.PRICE_OPTIMIZATION);
      if (!priceOptimizationModuleAdded) delete listEntries.priceOptimization;

      listData = generateListData(t, listEntries, '');
    }

    return listData;
  }, [offer, orderFlow, t, modules]);

  const showContent = Boolean(offer && !offerLoading && !offerError);
  const showAccordion = ![AppRoute.DATA_ENTRY, AppRoute.DATA_REVIEW].includes(currentRoute);

  return (
    <SideCard dataTestId={`${orderFlow}-offer-card`}>
      <div>
        <Typography.H2>{t('offer.title')}</Typography.H2>
      </div>

      {!mobile && <OfferLoadingOrError />}

      {showContent && (
        <div className={styles.offerCardContent} data-testid="offer-card-content">
          <TariffSummary
            consumption={productConfigurationsInput.expectedElectricityConsumptionInKwh || 0}
          />
          <SideCardButton loading={offerLoading} disabled={Boolean(offerError)} />
          {showAccordion && <AccordionList list={listEntries} dataTestId="offer" />}
        </div>
      )}
    </SideCard>
  );
};

export default memo(OfferCard);
